<script lang="ts">
  import type { Connector } from "./interfaces/connector.model";
  import { onMount } from "svelte";
  import { get } from "svelte/store";
  import { StorageKeys } from "./enums/localStorageKeys";
  import {
    accessToken,
    errorMessage,
    logout,
    selectedLanguageCode,
  } from "./store";
  import { LocalizationTexts, localizedTexts } from "./texts";
  import LanguageSwitcher from "./LanguageSwitcher.svelte";

  let texts: LocalizationTexts = localizedTexts.de;

  let merchantId = "";
  let connectorId = "";
  let logoUrl: string;

  let showSettings = false;

  onMount(() => {
    merchantId = localStorage.getItem(StorageKeys.MerchantId) ?? "";
    connectorId = localStorage.getItem(StorageKeys.ConnectorId) ?? "";
    trySetLogoUrlFromConnectorConfig();
    selectedLanguageCode.subscribe((language) => {
      texts = localizedTexts[language];
    });
  });

  function trySetLogoUrlFromConnectorConfig() {
    if (!connectorId) return;
    getConnector(connectorId).then((connector: Connector) => {
      logoUrl =
        connector?.schemaConfiguration?.hostedPagesConfiguration?.styles
          ?.logoUrl ?? "assets/icons/merchant-logo.svg";
    });
  }

  function getConnector(id: string): Promise<Connector> {
    return fetch(
      `FX_HOST_SAND/api/services/v2/Connectors/${id}?includeSchemaConfiguration=true`,
      {
        method: "get",
        headers: new Headers([
          ["Content-Type", "application/json"],
          ["Authorization", `Bearer ${get(accessToken)}`],
        ]),
      }
    )
      .then((res) => {
        if (res.ok) {
          return res.json();
        } else {
          if (res.status === 401) {
            logout;
          }
          errorMessage.set(
            (res.statusText ?? "Error occurred") + " - wrong connectorId?"
          );
          return undefined;
        }
      })
      .then((res: { connector: Connector }) => res?.connector ?? undefined);
  }

  function onSubmit(e) {
    const formData = new FormData(e.target);

    const data = {};
    for (let field of formData) {
      const [key, value] = field;
      if (!value) continue;
      data[key] = String(value).trim();
    }

    saveSettings(data[StorageKeys.MerchantId], data[StorageKeys.ConnectorId]);

    showSettings = false;
  }

  function saveSettings(merchantId: string, connectorId: string) {
    localStorage.setItem(StorageKeys.MerchantId, merchantId);
    localStorage.setItem(StorageKeys.ConnectorId, connectorId);
  }
</script>

<div class="flex flex-wrap justify-between items-center lg:px-20 ">
  <div class="lg:w-auto w-full flex justify-center py-4">
    <img src={logoUrl} class="h-20" alt="merchant-logo" />

  </div>
  <span
    class="flex flex-wrap justify-between w-full lg:w-auto lg:gap-x-12 font-bold text-gray-500 text-sm items-center"
  >
    <span>{texts.shop}</span>
    <span>{texts.aboutUs}</span>
    <span>{texts.contact}</span>
    <span>
      <button
        class="bg-transparent"
        on:click={() => (showSettings = !showSettings)}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
          />
        </svg>
      </button>
      {#if showSettings}
        <div
          class="absolute shadow-md p-4 rounded bg-white border border-gray-200"
        >
          <form on:submit|preventDefault={onSubmit} class="flex flex-col gap-2">
            <div>
              <label
                for="merchantId"
                class="block text-sm font-medium text-gray-700"
                >MerchantId</label
              >
              <input
                id="merchantId"
                name="merchantId"
                type="text"
                bind:value={merchantId}
                class="mt-1 text-xs p-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label
                for="connectorId"
                class="block text-sm font-medium text-gray-700"
                >ConnectorId</label
              >
              <input
                id="connectorId"
                name="connectorId"
                type="text"
                bind:value={connectorId}
                class="mt-1 text-xs p-2 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm border border-gray-300 rounded-md"
              />
            </div>
            <div class="flex gap-1">
              <button
                type="button"
                class="p-2 text-gray-500 flex-grow"
                on:click={() => (showSettings = false)}
              >
                Cancel
              </button>
              <button
                class="bg-indigo-500 p-2 rounded text-white flex-grow"
                type="submit"
              >
                Save
              </button>
            </div>
            <button
              class="bg-none text-indigo-500 text-center text-sm"
              on:click={logout}>Logout</button
            >
          </form>
        </div>
      {/if}
    </span>
    <LanguageSwitcher />
  </span>
</div>
